import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { t } from 'i18next';
import cn from 'classnames';
import { arrowBackOutline } from 'ionicons/icons';

import ElectonicReceiptItem from '../ElectronicReceiptItem';
import ElectronicReceiptForm from '../ElectronicReceiptForm';

import { arrayHasLength } from '~utils/array';
import { actionCreators as searchStoreActions } from '~redux/SearchStore/actions';
import { actionCreators as notificationActions } from '~redux/Notification/actions';
import { COMPANIES_TARGET, ACTUAL_RECEIPT_COMPANY } from '~redux/SearchStore/constants';
import { ELECTRONIC_RECEIPT_SIDE_MODAL } from '~redux/Modal/constants';
import { NOTIFICATION_DANGER } from '~redux/Notification/constants';
import { MAX_NUMBER_COMPANIES } from '~screens/Dashboard/components/ElectronicReceipt/constants';
import { useModal } from '~hooks/useModal';
import SimpleSpinner from '~components/SimpleSpinner';
import CustomButton from '~components/CustomButton';
import { ReactComponent as Building } from '~assets/buildings.svg';

import styles from './styles.module.scss';

function ElectronicReceiptList() {
  const [selectedCreateOrEditCompany, setSelectedCreateOrEditCompany] = useState(null);
  const { callCenterGuestUser } = useSelector(state => state.auth);
  const currentUser = useSelector(state => state.auth.currentUser);
  const companies = useSelector(state => state.searchStore[COMPANIES_TARGET]);
  const actualReceiptCompany = useSelector(state => state.searchStore[ACTUAL_RECEIPT_COMPANY]);
  const companiesLoading = useSelector(state => state.searchStore[`${COMPANIES_TARGET}Loading`]);
  const dispatch = useDispatch();
  const { handleCloseModal } = useModal(ELECTRONIC_RECEIPT_SIDE_MODAL);
  const isLoggedUser = currentUser || callCenterGuestUser;
  const [isLoadedCompanies, setIsLoadedCompanies] = useState(false);

  const handleBackButtonModal = () => {
    if (isLoggedUser && selectedCreateOrEditCompany) {
      setSelectedCreateOrEditCompany(null);
    } else {
      handleCloseModal();
    }
  };

  useEffect(() => {
    if (isLoggedUser && !isLoadedCompanies) {
      dispatch(searchStoreActions.getCompanies(callCenterGuestUser?.externalId));
      setIsLoadedCompanies(true);
    }
  }, [isLoggedUser, isLoadedCompanies]);

  const handleSelectCompany = company => {
    setSelectedCreateOrEditCompany(company);
  };

  const handleSaveCompany = data => {
    const companyData = {
      ...selectedCreateOrEditCompany,
      ...data,
      active: true
    };
    if (!companyData.id && companies?.length >= MAX_NUMBER_COMPANIES) {
      dispatch(
        notificationActions.show({
          message: t('ElectronicReceiptForm:maxCompaniesReached'),
          type: NOTIFICATION_DANGER
        })
      );
    } else {
      if (currentUser) {
        dispatch(searchStoreActions.saveCompany(companyData, callCenterGuestUser?.externalId));
      }
      dispatch(
        searchStoreActions.setSelectedCompanyForReceipt({ ...actualReceiptCompany, company: companyData })
      );
    }
    handleCloseModal();
  };

  const handleDeleteCompany = () => {
    dispatch(
      searchStoreActions.deleteCompany(selectedCreateOrEditCompany.id, callCenterGuestUser?.externalId)
    );
    handleBackButtonModal();
  };

  const showCompanies = () => {
    if (companiesLoading) {
      return <SimpleSpinner />;
    }
    if (companies && arrayHasLength(companies)) {
      return companies?.map(company => (
        <ElectonicReceiptItem
          key={`electonicReceiptItem-${company.id}`}
          company={company}
          onSelectCompany={handleSelectCompany}
        />
      ));
    }
    return (
      <>
        <Building />
        <div> {t('ElectronicReceiptForm:noCompanies')}</div>
      </>
    );
  };

  const showCompaniesLayout = () => (
    <div className={cn('column center', { [styles.addCompany]: !companies || companies.length === 0 })}>
      {showCompanies()}
      <CustomButton
        onClick={() => setSelectedCreateOrEditCompany({})}
        type="button"
        btnClassType="link"
        className={`button m-top-2 ${styles.addCompanyButton}`}>
        {t('ElectronicReceiptForm:addCompany')}
      </CustomButton>
    </div>
  );

  return (
    <div className="column">
      <CustomButton
        type="button"
        onClick={handleBackButtonModal}
        className={`m-bottom-3 ${styles.backButton}`}
        text={t('ElectronicReceiptForm:addCompany')}
        icon={arrowBackOutline}
        btnClassType="link"
      />
      {currentUser && !selectedCreateOrEditCompany ? (
        showCompaniesLayout()
      ) : (
        <ElectronicReceiptForm
          handleSaveCompany={handleSaveCompany}
          handleDeleteCompany={handleDeleteCompany}
          company={selectedCreateOrEditCompany}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}

export default ElectronicReceiptList;
